/**
 * The geometry behind ZivDoodles.
 *
 * Kept apart from the component so it is plain data with no React in it:
 * the illustration can then be rendered to a PNG by a build or review script
 * without a browser, and the mobile app can draw the same artwork through
 * react-native-svg from this one source.
 *
 * Every icon is one or more strokes in a 24×24 box, so they place and scale
 * uniformly. No fills anywhere — the whole illustration is outline.
 */

export const ICONS = {
  bubbleLines: [
    'M6 4h12a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z',
    'M7 8.5h10',
    'M7 12h6'
  ],
  bubble: [
    'M6 4h12a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z',
    'M8 16v5l5-5'
  ],
  heart: [
    'M12 20C7 16.2 4 13.2 4 10a3.8 3.8 0 0 1 8-2.6A3.8 3.8 0 0 1 20 10c0 3.2-3 6.2-8 10Z'
  ],
  star: [
    'M12 3.2l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17.2 6.6 20.1l1-6.1L3.2 9.7l6.1-.9Z'
  ],
  camera: [
    'M5 7h2.6l1.4-2h6l1.4 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z',
    'M12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z'
  ],
  music: [
    'M9 17.5V5l10-2v12.5',
    'M6.8 20.5a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z',
    'M16.8 18.5a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z'
  ],
  smiley: [
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
    'M8.4 13.8s1.3 2 3.6 2 3.6-2 3.6-2',
    'M9.2 9.3v1.2',
    'M14.8 9.3v1.2'
  ],
  bell: [
    'M18 16.5v-5a6 6 0 1 0-12 0v5l-2 2h16Z',
    'M10 21a2.2 2.2 0 0 0 4 0'
  ],
  clock: [
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
    'M12 7.2V12l3.4 2'
  ],
  pin: [
    'M12 21.2s7-6.4 7-11.2a7 7 0 1 0-14 0c0 4.8 7 11.2 7 11.2Z',
    'M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z'
  ],
  bike: [
    'M5.6 19.4a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2Z',
    'M18.4 19.4a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2Z',
    'M5.6 15.8 10.2 6.6h4.6l3.6 9.2',
    'M10.2 6.6 12.8 15.8'
  ],
  plane: [
    'M21 3 3 10.6l7 3 3 7Z',
    'M10 13.6 21 3'
  ],
  mic: [
    'M12 15.2a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6.2a3 3 0 0 0 3 3Z',
    'M6.4 11.4v.8a5.6 5.6 0 0 0 11.2 0v-.8',
    'M12 18.2V21'
  ],
  people: [
    'M8.6 11.4a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M3.4 19.4c0-2.9 2.3-5.2 5.2-5.2s5.2 2.3 5.2 5.2',
    'M15.6 5.8a3 3 0 0 1 0 5.6',
    'M16.4 14.5c2.4.4 4.2 2.4 4.2 4.9'
  ],
  cloud: [
    'M7.2 18.4h9.6a4.2 4.2 0 0 0 .5-8.4 6.2 6.2 0 0 0-11.8 1.7 3.6 3.6 0 0 0 1.7 6.7Z'
  ],
  pizza: [
    'M12 3.6 20.4 19.4a18.5 18.5 0 0 1-16.8 0Z',
    'M9.8 12.4h.1',
    'M13.8 14.6h.1',
    'M11.6 17.2h.1'
  ],
  iceCream: [
    'M8 10.6a4 4 0 1 1 8 0',
    'M7.4 10.6h9.2L12 21.2Z',
    // Waffle crosshatch: without it the dome-on-a-triangle reads as a map pin.
    'M9.4 13.8l1.6 1.6',
    'M12.4 13.8l1.6 1.6'
  ],
  donut: [
    'M12 20.4a8.4 8.4 0 1 0 0-16.8 8.4 8.4 0 0 0 0 16.8Z',
    'M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z'
  ],
  bolt: [
    'M13.4 2.4 4.6 14.2h6.2l-1 7.4 8.6-11.8h-6.2Z'
  ],
  envelope: [
    'M3.4 6.6h17.2v10.8H3.4Z',
    'M3.4 6.6 12 13l8.6-6.4'
  ],
  guitar: [
    'M8.2 21a4.8 4.8 0 1 0 0-9.6 4.8 4.8 0 0 0 0 9.6Z',
    // Soundhole and a two-line neck: a bare circle on a stick reads as a
    // magnifying glass, which is what the first draft looked like.
    'M8.2 17.7a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8Z',
    'M11 14.3 18.4 6.9',
    'M12.6 15.9 20 8.5',
    'M18.4 6.9 20 8.5l1.4-1.6-1.6-1.4Z'
  ],
  check: [
    'M4.2 13.2 9 18 19.8 6.4'
  ],
  sun: [
    'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z',
    'M12 2.5v2.2', 'M12 19.3v2.2', 'M2.5 12h2.2', 'M19.3 12h2.2',
    'M5.2 5.2l1.6 1.6', 'M17.2 17.2l1.6 1.6',
    'M5.2 18.8l1.6-1.6', 'M17.2 6.8l1.6-1.6'
  ],
  umbrella: [
    'M2.8 12a9.2 9.2 0 0 1 18.4 0Z',
    'M12 12v7.2a2.6 2.6 0 0 0 5.2 0'
  ],
  coffee: [
    'M4.5 8.5h11v6.5a4 4 0 0 1-4 4H8.5a4 4 0 0 1-4-4Z',
    'M15.5 10.5h2a2.6 2.6 0 0 1 0 5.2h-2',
    'M7.5 3.2v2.2', 'M11 3.2v2.2'
  ],
  bulb: [
    'M12 3.2a5.8 5.8 0 0 1 3.4 10.5c-.6.4-1 1.1-1 1.8v.3H9.6v-.3c0-.7-.4-1.4-1-1.8A5.8 5.8 0 0 1 12 3.2Z',
    'M9.8 18.2h4.4',
    'M10.6 20.8h2.8'
  ],
  ball: [
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
    'M12 7.4l3.6 2.6-1.4 4.2H9.8L8.4 10Z',
    'M12 3.2v4.2', 'M3.6 9.8l4.8.2', 'M20.4 9.8l-4.8.2'
  ],
  headphones: [
    'M4.2 15.5v-3.3a7.8 7.8 0 1 1 15.6 0v3.3',
    'M4.2 14.2h3v6.2H5.7a1.5 1.5 0 0 1-1.5-1.5Z',
    'M19.8 14.2h-3v6.2h1.5a1.5 1.5 0 0 0 1.5-1.5Z'
  ],
  leaf: [
    'M20.4 3.6S6.4 3.2 5.2 12.8c-.6 4.8 3.6 6.8 6.6 5.8 5.8-1.9 8.6-15 8.6-15Z',
    'M5.6 20.4S9.4 13.6 16 9.4'
  ],
  crown: [
    'M3.6 7.2 8.6 11 12 4.8 15.4 11l5-3.8-1.4 9.6H5Z',
    'M5.6 20.4h12.8'
  ],
  key: [
    'M15.6 8.4a4.6 4.6 0 1 1-9.2 0 4.6 4.6 0 0 1 9.2 0Z',
    'M11 13v8.2', 'M11 16.6h3.2', 'M11 19.4h2.4'
  ],
  ticket: [
    'M3.8 9.4a2.3 2.3 0 0 0 0 5.2v2.8h16.4v-2.8a2.3 2.3 0 0 1 0-5.2V6.6H3.8Z',
    'M12 7.6v1.8', 'M12 11.2v1.6', 'M12 14.6v1.8'
  ],
  rocket: [
    'M12 2.6s4.8 3.2 4.8 9c0 2.9-1.9 5.8-4.8 8.6-2.9-2.8-4.8-5.7-4.8-8.6 0-5.8 4.8-9 4.8-9Z',
    'M12 11.8a2.1 2.1 0 1 0 0-4.2 2.1 2.1 0 0 0 0 4.2Z',
    'M7.6 15.4 5 20.6l4.2-2',
    'M16.4 15.4 19 20.6l-4.2-2'
  ],
  gift: [
    'M3.6 9.6h16.8v3.6H3.6Z',
    'M5.4 13.2v7.4h13.2v-7.4',
    'M12 9.6v11',
    'M12 9.6c-2.6 0-4.2-.9-4.2-2.4S10.6 5.4 12 9.6Z',
    'M12 9.6c2.6 0 4.2-.9 4.2-2.4S13.4 5.4 12 9.6Z'
  ]
}

/**
 * Where each icon sits: `a` is the angle in degrees clockwise from the top,
 * `r` the distance from centre, `s` the scale.
 *
 * The radii are jittered by a few pixels on purpose. On a perfectly even ring
 * the eye reads a clock face; the small irregularity is what makes it read as
 * a hand-drawn scatter instead.
 */
export const PLACEMENT = [
  // Inner ring — the largest icons, closest to the phone.
  { icon: 'bubbleLines', a: -90,  r: 100, s: 1.38 },
  { icon: 'camera',      a: -45,  r: 104, s: 1.32 },
  { icon: 'heart',       a: 0,    r: 99,  s: 1.28 },
  { icon: 'music',       a: 45,   r: 103, s: 1.34 },
  { icon: 'smiley',      a: 90,   r: 100, s: 1.36 },
  { icon: 'pin',         a: 135,  r: 104, s: 1.30 },
  { icon: 'star',        a: 180,  r: 99,  s: 1.24 },
  { icon: 'bubble',      a: -135, r: 103, s: 1.34 },

  // Middle ring — 13 of them, offset from the inner eight so the icons
  // interlock rather than lining up into spokes.
  { icon: 'plane',       a: -90,  r: 148, s: 1.20 },
  { icon: 'bolt',        a: -62,  r: 143, s: 1.10 },
  { icon: 'guitar',      a: -35,  r: 150, s: 1.24 },
  { icon: 'bell',        a: -7,   r: 145, s: 1.16 },
  { icon: 'pizza',       a: 21,   r: 149, s: 1.20 },
  { icon: 'bike',        a: 49,   r: 144, s: 1.28 },
  { icon: 'donut',       a: 76,   r: 150, s: 1.16 },
  { icon: 'mic',         a: 104,  r: 145, s: 1.16 },
  { icon: 'people',      a: 132,  r: 149, s: 1.24 },
  { icon: 'cloud',       a: 159,  r: 144, s: 1.20 },
  { icon: 'iceCream',    a: 187,  r: 150, s: 1.18 },
  { icon: 'envelope',    a: 215,  r: 145, s: 1.16 },
  { icon: 'clock',       a: 242,  r: 149, s: 1.20 },

  // Outer ring — 14 smaller icons closing the circle's edge.
  { icon: 'sun',         a: -77,  r: 182, s: 1.04 },
  { icon: 'umbrella',    a: -51,  r: 178, s: 1.08 },
  { icon: 'coffee',      a: -26,  r: 183, s: 1.02 },
  { icon: 'bulb',        a: 0,    r: 179, s: 1.06 },
  { icon: 'ball',        a: 26,   r: 183, s: 1.02 },
  { icon: 'headphones',  a: 51,   r: 178, s: 1.08 },
  { icon: 'leaf',        a: 77,   r: 182, s: 1.04 },
  { icon: 'crown',       a: 103,  r: 178, s: 1.06 },
  { icon: 'key',         a: 129,  r: 183, s: 1.02 },
  { icon: 'ticket',      a: 154,  r: 179, s: 1.06 },
  { icon: 'rocket',      a: 180,  r: 183, s: 1.04 },
  { icon: 'gift',        a: 206,  r: 178, s: 1.06 },
  { icon: 'check',       a: 231,  r: 182, s: 1.00 },
  { icon: 'star',        a: 257,  r: 179, s: 1.04 }
]

/**
 * Small dots filling what is left between the rings. The reference is dense
 * enough that the eye reads a filled disc rather than three orbits, and these
 * are what close the remaining gaps.
 */
export const SPECKS = [
  { a: -104, r: 124, d: 2.8 }, { a: -70, r: 121, d: 2.2 },
  { a: -22,  r: 126, d: 2.6 }, { a: 14,  r: 122, d: 2.2 },
  { a: 62,   r: 125, d: 2.8 }, { a: 110, r: 121, d: 2.2 },
  { a: 148,  r: 126, d: 2.6 }, { a: 196, r: 123, d: 2.4 },
  { a: -148, r: 125, d: 2.8 }, { a: 232, r: 122, d: 2.2 },

  { a: -83,  r: 166, d: 2.4 }, { a: -44, r: 163, d: 2.0 },
  { a: -15,  r: 167, d: 2.6 }, { a: 36,  r: 164, d: 2.2 },
  { a: 88,   r: 166, d: 2.4 }, { a: 118, r: 163, d: 2.0 },
  { a: 170,  r: 167, d: 2.6 }, { a: 224, r: 164, d: 2.2 },

  { a: -64,  r: 196, d: 2.2 }, { a: 12,  r: 198, d: 2.6 },
  { a: 66,   r: 196, d: 2.0 }, { a: 140, r: 198, d: 2.4 },
  { a: 194,  r: 196, d: 2.2 }, { a: 246, r: 197, d: 2.0 }
]

/** The phone at the centre, drawn directly in viewBox coordinates. */
export const PHONE = [
  // body
  'M182 144h56a10 10 0 0 1 10 10v112a10 10 0 0 1-10 10h-56a10 10 0 0 1-10-10V154a10 10 0 0 1 10-10Z',
  // earpiece and home indicator
  'M202 153h16',
  'M200 266h20',
  // the ZIvapp mark: a speech bubble holding a Z
  'M190 180h40a8 8 0 0 1 8 8v22a8 8 0 0 1-8 8h-24l-10 9v-9h-6a8 8 0 0 1-8-8v-22a8 8 0 0 1 8-8Z'
]

/** Drawn heavier than the rest of the phone so the mark reads at small sizes. */
export const PHONE_MARK = 'M201 191h18l-18 15h18'

export const VIEWBOX = 420
export const CENTRE = VIEWBOX / 2

/** Polar to cartesian, with 0° at the top rather than the right. */
export function at (angle, radius) {
  const rad = ((angle - 90) * Math.PI) / 180
  return { x: CENTRE + radius * Math.cos(rad), y: CENTRE + radius * Math.sin(rad) }
}

/** The transform that places one icon's 24×24 box at its spot on the ring. */
export function placementTransform ({ a, r, s }) {
  const { x, y } = at(a, r)
  return `translate(${x.toFixed(1)} ${y.toFixed(1)}) scale(${s}) translate(-12 -12)`
}
